import type { Animal } from "../animals";
import {
  RECT,
  L,
  R,
  T,
  B,
  W,
  rectPath,
  frame,
  colorStep,
  flag,
  circle,
  striped,
  clipPoly,
  n,
} from "../flags";

// World Cup 2026 — second batch of African (CAF) qualifiers. Drawn in the same
// 3:2 flag box as every other flag (L,T,R,B / RECT). Coloured regions are
// outlined BEFORE the colour step; white counts as paper but is still authored.

// 1. Ivory Coast — vertical orange / white / green tricolour.
const ivoryCoast = striped(
  "flag-ivory-coast",
  "Ivory Coast",
  "🇨🇮",
  "v",
  [{ color: "#FF8200" }, { color: "#ffffff" }, { color: "#009E60" }],
  "Ivory Coast's flag is orange, white and green.",
);

// 2. Morocco — red field with a green five-point star (pentagram) centred.
const morocco = (() => {
  // Green five-point star, shaped in the Step Editor (sharper points than the
  // default) — kept as a literal so it matches what was verified.
  const moroStar =
    "M 100,70 L 108.3,90.3 L 128.5,90.7 L 114.3,104.6 L 117.6,124.3 L 100.8,112.2 L 82.4,124.3 L 85.7,104.6 L 71.5,90.7 L 92.4,91 Z";
  // The red field as a DONUT: the whole box with the star punched out (even-odd),
  // so colouring the field fills AROUND the already-green star. The star is
  // coloured green in its own draw step, so it's green the whole time — there's
  // never a step where it sits as a blank white star (which is what happens if
  // the field colours first and reserves the star as paper). Final image is
  // identical: green star on a red field.
  const redField = `${RECT} ${moroStar}`;
  return flag(
    "flag-morocco",
    "Morocco",
    "🇲🇦",
    [
      frame(),
      // Draw the star AND fill it green right here, so it reads green from the
      // start (the green fill sits behind the thin outline as it's traced).
      {
        hint: "Draw a green five-point star ⭐",
        color: "#3a3a55",
        strokes: [moroStar],
        strokeWidth: 0.7,
        fills: [{ d: moroStar, color: "#006233" }],
      },
      // Colour ONLY the red field, around the green star (even-odd hole). One
      // colour means no split, so the star never flashes as a blank white hole.
      colorStep([{ d: redField, color: "#C1272D", evenodd: true }]),
    ],
    "Morocco's flag has a green star.",
  );
})();

// 3. Senegal — green / yellow / red vertical tricolour with a green star on the
// yellow middle band.
const senegal = (() => {
  // Green star reshaped in the Step Editor (sharper points) — kept as a literal.
  const senStar =
    "M 100,78 L 105.6,93.3 L 120.9,93.2 L 110.5,103.4 L 112.9,117.8 L 100.3,107.9 L 87.1,117.8 L 89.5,103.4 L 79.1,93.2 L 94.5,93.3 Z";
  // Three equal vertical bands.
  const b1 = L + W / 3,
    b2 = L + (2 * W) / 3;
  return flag(
    "flag-senegal",
    "Senegal",
    "🇸🇳",
    [
      frame(),
      {
        hint: "Add 3 stripes up and down",
        color: "#3a3a55",
        strokes: [`M ${b1},${T} L ${b1},${B}`, `M ${b2},${T} L ${b2},${B}`],
      },
      { hint: "Draw a five-point star in the middle ⭐", color: "#3a3a55", strokes: [senStar], strokeWidth: 0.7 },
      colorStep([
        { d: rectPath(L, T, b1, B), color: "#00853F" },
        { d: rectPath(b1, T, b2, B), color: "#FDEF42" },
        { d: rectPath(b2, T, L + W, B), color: "#E31B23" },
        { d: senStar, color: "#00853F" },
      ]),
    ],
    "Senegal's flag has a green star.",
  );
})();

// 4. South Africa — the famous Y / pall flag. Geometry mapped 1:1 from the
// official Flag of South Africa SVG (a 90×60 design) into our 180×120 box: every
// ref unit → 2 of ours, so the skeleton is topleft (10,40), botleft (10,160),
// fork (100,100) and fly-centre (190,100). The Y is drawn the way the real flag
// is — as CONSTANT-WIDTH bands swept along that skeleton — so the green arms are
// the same thickness everywhere (the previous hand-traced hexagons pinched). The
// stacking (document order = paint order) matches the SVG: red top + blue bottom,
// then the white pall (sw 40), then the black triangle, the gold rim over its
// edge, and finally the green pall (sw 24) on top. The 8-unit white border on the
// fly side and the gold border on the hoist side are the leftover rims of the
// wider white band peeking out past the narrower green Y.
const southAfrica = (() => {
  type Pt = [number, number];
  const cy = (T + B) / 2; // 100
  const K: Pt = [L + W * 0.5, cy]; // fork (100,100)
  const FC: Pt = [R, cy]; // fly centre (190,100)
  const TL: Pt = [L, T]; // top-hoist corner (10,40)
  const BL: Pt = [L, B]; // bottom-hoist corner (10,160)

  const unit = (a: Pt, b: Pt): Pt => {
    const dx = b[0] - a[0],
      dy = b[1] - a[1],
      l = Math.hypot(dx, dy);
    return [dx / l, dy / l];
  };
  const perp = (u: Pt): Pt => [-u[1], u[0]];
  const uH = unit(K, FC); // horizontal arm → fly
  const uU = unit(K, TL); // upper diagonal arm → top-hoist corner
  const uLo = unit(K, BL); // lower diagonal arm → bottom-hoist corner

  const box: Pt[] = [
    [L, T],
    [R, T],
    [R, B],
    [L, B],
  ];
  const toPath = (pts: Pt[]) =>
    "M " + pts.map(([x, y]) => `${n(x)},${n(y)}`).join(" L ") + " Z";
  // An edge line of a band: the centre line (through K along u) shifted s·hw
  // along the perpendicular. Returned as a point + direction.
  const edge = (u: Pt, s: number, hw: number) => {
    const q = perp(u);
    return { p: [K[0] + q[0] * s * hw, K[1] + q[1] * s * hw] as Pt, d: u };
  };
  // Intersection of two infinite lines (point + direction each).
  const meet = (a: { p: Pt; d: Pt }, b: { p: Pt; d: Pt }): Pt => {
    const den = a.d[0] * -b.d[1] - a.d[1] * -b.d[0];
    const t = ((b.p[0] - a.p[0]) * -b.d[1] - (b.p[1] - a.p[1]) * -b.d[0]) / den;
    return [a.p[0] + t * a.d[0], a.p[1] + t * a.d[1]];
  };
  const atX = (e: { p: Pt; d: Pt }, x: number): Pt => {
    const t = (x - e.p[0]) / e.d[0];
    return [x, e.p[1] + t * e.d[1]];
  };

  // The pall (half-width hw) as ONE clean Y polygon, traced around its boundary.
  // Every fork corner is the exact MITER intersection of the two adjacent OUTER
  // band edges — so the width is constant and the borders meet in sharp points
  // (no stepped notches). The hoist side dips in through the wedge apex (where the
  // two diagonal arms' INNER edges cross), keeping the hoist wedge OPEN for the
  // black triangle instead of filling it solid. Ends are taken past the frame at
  // x = L (and clipped to the box afterwards) so the arms meet the hoist cleanly.
  const pall = (hw: number): Pt[] => {
    const hTop = edge(uH, -1, hw); // horizontal arm, top edge (y = cy − hw)
    const hBot = edge(uH, +1, hw); // horizontal arm, bottom edge
    // For each diagonal arm, "outer" = the side further from the centre line
    // (toward the flag's top/bottom corner), "inner" = the side facing the
    // horizontal arm / the wedge.
    const upSide = perp(uU)[1] < 0 ? +1 : -1;
    const upOut = edge(uU, upSide, hw),
      upIn = edge(uU, -upSide, hw);
    const loSide = perp(uLo)[1] > 0 ? +1 : -1;
    const loOut = edge(uLo, loSide, hw),
      loIn = edge(uLo, -loSide, hw);
    return [
      atX(upOut, L), // up-arm OUTER edge at the hoist (top-left)
      meet(upOut, hTop), // top fork miter (outer)
      atX(hTop, R), // horizontal top edge at the fly
      atX(hBot, R), // horizontal bottom edge at the fly
      meet(loOut, hBot), // bottom fork miter (outer)
      atX(loOut, L), // lo-arm OUTER edge at the hoist (bottom-left)
      atX(loIn, L), // lo-arm INNER edge at the hoist
      meet(loIn, upIn), // inner wedge apex (between the diagonals)
      atX(upIn, L), // up-arm INNER edge at the hoist
    ];
  };
  const clipBox = (pts: Pt[]) => toPath(clipPoly(pts, box));

  // Green Y: ref sw 12 → half-width 12. White pall: ref sw 20 → half-width 20, so
  // it pokes 8 units past the green on every side (that rim is the fimbriation).
  const greenPall = clipBox(pall(12));
  const whitePall = clipBox(pall(20));

  // Black hoist triangle (sitting in the wedge), plus the gold rim = the SAME
  // white-width pall clipped to the triangle. That keeps only the white band's
  // hoist-side rims (the parts inside the triangle), so gold shows ONLY on the
  // hoist side; the band's fly-side rims stay white. Black fills the wedge under
  // it, and the narrower green Y (12) painted on top covers all but the 8-unit
  // gold rim — exactly as thin as the white fimbriation.
  const tri: Pt[] = [TL, K, BL];
  // Black triangle inset from the corners (tuned in the Step Editor) so the gold
  // rim shows around it; the gold rim still clips the white pall to the full
  // corner wedge `tri`, so only the black itself is the smaller shape.
  const blackTri = "M 10.7,64.9 L 61.8,99.9 L 10.1,135.1 Z";
  const goldRim = toPath(clipPoly(pall(20), tri));

  const redRegion = rectPath(L, T, R, cy);
  const blueRegion = rectPath(L, cy, R, B);

  return flag(
    "flag-south-africa",
    "South Africa",
    "🇿🇦",
    [
      frame(),
      // Outline the GREEN Y itself first — drawn in its own green so the guide
      // line vanishes into the final green fill (no stray dark outline on the
      // stripe). It's the NARROWER pall (hw 12); the thin white fimbriation is the
      // 8-unit paper rim left between this green and the red/blue field (and the
      // gold rim on the hoist side), revealed automatically at colour time. So the
      // green stripes are drawn as line-art up front instead of only appearing
      // when colouring.
      { hint: "Draw a sideways green Y across the flag", color: "#007749", strokes: [greenPall] },
      { hint: "Draw a triangle at the left edge 🔺", color: "#3a3a55", strokes: [blackTri] },
      colorStep([
        { d: redRegion, color: "#E03C31" }, // top band (above the Y)
        { d: blueRegion, color: "#001489" }, // bottom band (below the Y)
        { d: whitePall, color: "#ffffff" }, // white pall (border under the green)
        { d: blackTri, color: "#000000" }, // black hoist triangle
        { d: goldRim, color: "#FFB81C" }, // gold rim over the triangle edge
        { d: greenPall, color: "#007749" }, // green Y on top — constant width
      ]),
    ],
    "South Africa's flag has six colours in a Y shape.",
  );
})();

// 5. Tunisia — red field, white centre circle, with a red crescent (opens right)
// and a red five-point star inside the circle.
const tunisia = (() => {
  const cx = 100,
    cy = 100;
  const disc = circle(cx, cy, 30);
  // Crescent + star reshaped/placed in the Step Editor — kept as literals so they
  // match what was verified (the crescent is slightly elliptical from the two-
  // circle editor).
  const cres =
    "M 116.8,113.4 A 21.3,21.6 0 1,1 116.1,85.5 A 17.5,17.9 0 1,0 116.8,113.4 Z";
  const tunStar =
    "M 103.1,88.7 L 108,94.1 L 115.2,93.1 L 111.6,99.5 L 114.8,106 L 107.6,104.5 L 102.3,109.6 L 101.6,102.3 L 95.1,98.9 L 101.8,95.9 Z";
  return flag(
    "flag-tunisia",
    "Tunisia",
    "🇹🇳",
    [
      frame(),
      { hint: "Draw a big circle in the middle ⭕", color: "#3a3a55", strokes: [disc] },
      { hint: "Draw a crescent moon 🌙", color: "#3a3a55", strokes: [cres] },
      { hint: "Add a five-point star ⭐", color: "#3a3a55", strokes: [tunStar], strokeWidth: 0.7 },
      colorStep([
        { d: RECT, color: "#E70013" },
        { d: disc, color: "#ffffff" },
        { d: cres, color: "#E70013" },
        { d: tunStar, color: "#E70013" },
      ]),
    ],
    "Tunisia's flag has a red moon and star in a white circle.",
  );
})();

export const caf2Flags: Animal[] = [ivoryCoast, morocco, senegal, southAfrica, tunisia];
